// @ts-nocheck
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import { processFieldValue, UOMScale } from "../utils/uomProcessor";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a financial value, handling both numeric values and string values like "Not Available"
 * @param value - The financial value to format
 * @param prefix - Optional prefix (like '$') to add before numeric values
 * @param suffix - Optional suffix (like '%') to add after numeric values
 * @returns Formatted string representation of the value
 */
export function formatFinancialValue(
  value: number | string,
  prefix = "",
  suffix = ""
): string {
  // Handle "Not Available" or other string values
  if (typeof value === "string") {
    return value;
  }

  // Handle numeric values with toFixed and prefix/suffix
  if (typeof value === "number") {
    return `${prefix}${value.toFixed(2)}${suffix}`;
  }

  // Fallback for unexpected types
  return "N/A";
}

/**
 * Enhanced financial value formatter with UOM support
 * @param value - The value to format
 * @param fieldName - The field name for UOM lookup
 * @param uomScale - The UOM scale object
 * @param fallbackPrefix - Fallback prefix if no UOM info
 * @param fallbackSuffix - Fallback suffix if no UOM info
 * @returns Formatted string representation
 */
export function formatFinancialValueWithUOM(
  value: number | string,
  fieldName: string,
  uomScale?: UOMScale,
  fallbackPrefix = "",
  fallbackSuffix = ""
): string {
  // Try UOM formatting first
  if (uomScale && fieldName) {
    const processed = processFieldValue(value, fieldName, uomScale);
    if (processed.unit) {
      return processed.formatted;
    }
  }

  // Fallback to original formatting
  return formatFinancialValue(value, fallbackPrefix, fallbackSuffix);
}
